import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Form, Table, Badge, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import TenantManagementLayout from './TenantManagementLayout';

const ImpersonationPanel = ({ auth }) => {
  const [tenants, setTenants] = useState([]);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ enabled: true });
  const [currentSession, setCurrentSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedRole, setSelectedRole] = useState('user');
  const [impersonateReason, setImpersonateReason] = useState('');
  const [showStartModal, setShowStartModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch tenants
      const tenantsResponse = await fetch('/api/admin/tenants', { credentials: 'include' });
      if (tenantsResponse.ok) {
        const data = await tenantsResponse.json();
        setTenants(data.tenants || []);
      }

      // Fetch current impersonation status
      const statusResponse = await fetch('/api/admin/impersonate/status', { credentials: 'include' });
      if (statusResponse.ok) {
        const data = await statusResponse.json();
        if (data.impersonation) {
          setCurrentSession(data.impersonation);
        }
      }

      // Fetch impersonation history
      const historyResponse = await fetch('/api/admin/impersonation-history', { credentials: 'include' });
      if (historyResponse.ok) {
        const data = await historyResponse.json();
        setHistory(data.logs || []);
      }

      // Fetch impersonation settings (for super_admin)
      if (auth.platform_role === 'super_admin') {
        const settingsResponse = await fetch('/api/admin/impersonation-settings', { credentials: 'include' });
        if (settingsResponse.ok) {
          const data = await settingsResponse.json();
          setSettings(data);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedTenant) {
      setError('Please select a tenant');
      return;
    }

    try {
      const response = await fetch(`/api/admin/tenants/${selectedTenant}/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role: selectedRole,
          reason: impersonateReason
        })
      });

      if (response.ok) {
        // Redirect to dashboard as impersonated user
        window.location.href = '/dashboard';
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start impersonation');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEndImpersonation = async () => {
    try {
      const response = await fetch('/api/admin/impersonate', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Impersonation session ended');
        setCurrentSession(null);
        fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to end impersonation');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleImpersonation = async () => {
    try {
      const response = await fetch('/api/admin/impersonation-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !settings.enabled })
      });

      if (response.ok) {
        setSettings({ ...settings, enabled: !settings.enabled });
        setSuccess(`Impersonation ${!settings.enabled ? 'enabled' : 'disabled'}`);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update settings');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const getRoleOptions = () => {
    // Platform managers can only impersonate as user (view-only)
    if (auth.platform_role === 'manager') {
      return [{ value: 'user', label: 'User (View Only)' }];
    }
    return [
      { value: 'admin', label: 'Admin' },
      { value: 'manager', label: 'Manager' },
      { value: 'user', label: 'User' }
    ];
  };

  if (loading) {
    return (
      <TenantManagementLayout title="Impersonation">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading...</p>
        </div>
      </TenantManagementLayout>
    );
  }

  return (
    <TenantManagementLayout
      title="Impersonation"
      subtitle="View tenant data as different user roles"
    >
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
          {success}
        </Alert>
      )}

      {/* Current Session Banner */}
      {currentSession && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Active Impersonation Session</strong>
            <br />
            Viewing <strong>{currentSession.tenantName}</strong> as <Badge bg="info">{currentSession.role}</Badge>
            <br />
            <small>
              Started: {formatDate(currentSession.startedAt)} |
              Remaining: {currentSession.remainingMinutes} minutes
            </small>
          </div>
          <Button variant="danger" onClick={handleEndImpersonation}>
            End Session
          </Button>
        </Alert>
      )}

      {/* Kill Switch (Super Admin Only) */}
      {auth.platform_role === 'super_admin' && (
        <Card className="mb-4" style={{ borderColor: settings.enabled ? '#22c55e' : '#ef4444' }}>
          <Card.Body className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">Impersonation System</h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                {settings.enabled
                  ? 'Impersonation is enabled for platform admins'
                  : 'Impersonation is disabled (kill switch active)'}
              </p>
            </div>
            <Button
              variant={settings.enabled ? 'outline-danger' : 'success'}
              onClick={handleToggleImpersonation}
            >
              {settings.enabled ? 'Disable' : 'Enable'}
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Start Impersonation */}
      {!currentSession && settings.enabled && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Start Impersonation</h5>
          </Card.Header>
          <Card.Body>
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              Select a tenant and role to view their data. Impersonation sessions automatically end after 30 minutes.
              All actions are logged for audit purposes.
            </p>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Tenant</Form.Label>
                  <Form.Select
                    value={selectedTenant}
                    onChange={(e) => setSelectedTenant(e.target.value)}
                  >
                    <option value="">Select a tenant...</option>
                    {tenants.filter(t => t.is_active).map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    {getRoleOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Reason (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={impersonateReason}
                    onChange={(e) => setImpersonateReason(e.target.value)}
                    placeholder="Support ticket #123"
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end mb-3">
                <Button
                  variant="primary"
                  onClick={() => setShowStartModal(true)}
                  disabled={!selectedTenant}
                >
                  Start
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Impersonation History */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Impersonation History</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={{ padding: '1rem' }}>Date</th>
                <th style={{ padding: '1rem' }}>Impersonator</th>
                <th style={{ padding: '1rem' }}>Tenant</th>
                <th style={{ padding: '1rem' }}>Role</th>
                <th style={{ padding: '1rem' }}>Duration</th>
                <th style={{ padding: '1rem' }}>Actions</th>
                <th style={{ padding: '1rem' }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    No impersonation history
                  </td>
                </tr>
              ) : (
                history.map((log, idx) => {
                  const duration = log.ended_at
                    ? Math.round((new Date(log.ended_at) - new Date(log.started_at)) / 60000)
                    : null;
                  return (
                    <tr key={idx}>
                      <td style={{ padding: '1rem' }}>{formatDate(log.started_at)}</td>
                      <td style={{ padding: '1rem' }}>
                        <div>
                          {log.impersonator?.email || 'Unknown'}
                          <br />
                          <small className="text-muted">{log.impersonator_platform_role}</small>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>{log.targetTenant?.name || 'Unknown'}</td>
                      <td style={{ padding: '1rem' }}>
                        <Badge bg="info">{log.impersonated_role}</Badge>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {duration !== null ? `${duration} min` : (
                          <Badge bg="warning">Active</Badge>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {log.actions_count || 0}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {log.reason || '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal show={showStartModal} onHide={() => setShowStartModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Start Impersonation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            You are about to impersonate a user in <strong>{tenants.find(t => t.id === selectedTenant)?.name}</strong> as <strong>{selectedRole}</strong>.
          </Alert>
          <p>This will:</p>
          <ul>
            <li>Allow you to view data as if you were a {selectedRole} in that tenant</li>
            <li>Log this session for audit purposes</li>
            <li>Automatically end after 30 minutes</li>
          </ul>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Note: During impersonation, certain destructive actions (deleting accounts, changing passwords) are blocked.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStartModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            setShowStartModal(false);
            handleStartImpersonation();
          }}>
            Start Impersonation
          </Button>
        </Modal.Footer>
      </Modal>
    </TenantManagementLayout>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(ImpersonationPanel);
